/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [tenantFilter, setTenantFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const { user } = useAuth();

  // Reset password state
  const [resetPasswordData, setResetPasswordData] = useState({
    userId: null,
    newPassword: '',
    confirmPassword: ''
  });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Form states
  const [bulkAssignment, setBulkAssignment] = useState({
    userIds: [],
    tenantId: '',
    accessLevel: 'Read'
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  
  // Password mismatch states
  const [passwordMismatches, setPasswordMismatches] = useState({});
  const [checkingMismatches, setCheckingMismatches] = useState(false);
  const [resolvingMismatch, setResolvingMismatch] = useState(null);
  
  // Data conflict states
  const [dataConflicts, setDataConflicts] = useState({});
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [resolvingConflicts, setResolvingConflicts] = useState(false);
  
  // Role configuration state
  const [roleConfig, setRoleConfig] = useState({ roleHierarchy: {}, permissions: {} });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoleConfig, setLoadingRoleConfig] = useState(false);

  useEffect(() => {
    if (user) {
      loadUsers();
      loadTenants();
      loadRoleConfig();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/auth/users');
      const data = response.data;
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    }
  };

  const loadTenants = async () => {
    try {
      const response = await axios.get('/api/tenants');
      const data = response.data;
      setTenants(data.tenants || []);
    } catch (error) {
      console.error('Error loading tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoleConfig = async () => {
    try {
      setLoadingRoleConfig(true);
      // Load role configuration (hierarchy and permissions)
      const configResponse = await axios.get('/api/tenant-roles/roles/config');
      if (configResponse.data?.success && configResponse.data.config) {
        setRoleConfig(configResponse.data.config);
      }
      
      // Load available roles from database (for role assignment)
      try {
        const rolesResponse = await axios.get('/api/settings/roles');
        if (rolesResponse.data?.roles) {
          // Sort roles by hierarchy if available
          const sortedRoles = rolesResponse.data.roles.sort((a, b) => {
            const hierarchyA = configResponse.data?.config?.roleHierarchy?.[a.RoleName] || 0;
            const hierarchyB = configResponse.data?.config?.roleHierarchy?.[b.RoleName] || 0;
            return hierarchyB - hierarchyA; // Higher hierarchy first
          });
          setAvailableRoles(sortedRoles);
        }
      } catch (rolesError) {
        console.warn('Could not load available roles:', rolesError);
        // Fallback: extract roles from config if API fails
        if (configResponse.data?.config?.roleHierarchy) {
          const rolesFromConfig = Object.keys(configResponse.data.config.roleHierarchy).map(roleName => ({
            RoleName: roleName,
            RoleID: null // Will be fetched from database when needed
          }));
          setAvailableRoles(rolesFromConfig);
        }
      }
    } catch (error) {
      console.error('Error loading role configuration:', error);
    } finally {
      setLoadingRoleConfig(false);
    }
  };

  // Helper function to get role display name with hierarchy info
  const getRoleDisplayName = (roleName) => {
    if (!roleName) return 'No Role';
    const hierarchy = roleConfig.roleHierarchy?.[roleName];
    if (hierarchy !== undefined) {
      return `${roleName} (Level ${hierarchy})`;
    }
    return roleName;
  };

  // Helper function to sort roles by hierarchy
  const sortRolesByHierarchy = (roles) => {
    if (!roles || !Array.isArray(roles)) return [];
    return [...roles].sort((a, b) => {
      const roleNameA = a.RoleName || a.roleName || a;
      const roleNameB = b.RoleName || b.roleName || b;
      const hierarchyA = roleConfig.roleHierarchy?.[roleNameA] || 0;
      const hierarchyB = roleConfig.roleHierarchy?.[roleNameB] || 0;
      return hierarchyB - hierarchyA; // Higher hierarchy first
    });
  };

  // User tenant data is already included in the user objects from /api/auth/users
  const getUserTenants = (user) => {
    return user.tenants || [];
  };

  const handleBulkAssignment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/api/tenants/bulk-assign', bulkAssignment);

      const data = response.data;
      
      // Show results
      const successCount = data.summary.successful;
      const failedCount = data.summary.failed;
      
      if (failedCount === 0) {
        alert(`Successfully assigned ${successCount} users to repository.`);
      } else {
        alert(`Assigned ${successCount} users successfully. ${failedCount} assignments failed.`);
      }

      // Reset form
      setBulkAssignment({
        userIds: [],
        tenantId: '',
        accessLevel: 'Read'
      });

      setActiveModal(null);
      await loadUsers();

    } catch (error) {
      console.error('Error in bulk assignment:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserFromTenant = (userId, tenantId) => {
    setConfirmAction({
      title: 'Remove User from Tenant',
      message: 'Are you sure you want to remove this user from the tenant?',
      action: async () => {
        try {
          const tenant = tenants.find(t => t.id === tenantId);
          const response = await axios.delete(`/api/tenants/${tenant.code}/users/${userId}`);

          // Refresh user data to get updated tenant assignments
          await loadUsers();
          
          // Update selected user if it's currently displayed
          if (selectedUser && selectedUser.id === userId) {
            const updatedUser = users.find(u => u.id === userId);
            if (updatedUser) {
              setSelectedUser(updatedUser);
            }
          }

        } catch (error) {
          console.error('Error removing user from tenant:', error);
          setError(error.message);
        }
        setConfirmAction(null);
      }
    });
  };

  const handleDeleteUser = (user) => {
    const userTenants = getUserTenants(user);
    
    if (userTenants.length === 0) {
      // User has no tenants, show simple confirmation
      setConfirmAction({
        title: 'Delete User',
        message: `Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`,
        action: async () => {
          try {
            const response = await axios.delete(`/api/auth/users/${user.id}`);

            await loadUsers();
            setActiveModal(null);
            
          } catch (error) {
            console.error('Error deleting user:', error);
            setError(error.message);
          }
          setConfirmAction(null);
        }
      });
    } else {
      // User has tenants, show deletion options modal
      setActiveModal('deleteOptions');
      setSelectedUser(user);
    }
  };

  // Open reset password modal
  const handleOpenResetPassword = (user) => {
    setResetPasswordData({ userId: user.id, newPassword: '', confirmPassword: '' });
    setResetError('');
    setResetSuccess('');
    setActiveModal('resetPassword');
  };

  // Submit reset password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    try {
      // Basic validation
      if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
        setResetError('Please enter and confirm the new password.');
        return;
      }
      if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
        setResetError('Passwords do not match.');
        return;
      }
      if (resetPasswordData.newPassword.length < 8) {
        setResetError('Password must be at least 8 characters.');
        return;
      }

      setResetLoading(true);
      await axios.post(`/api/auth/users/${resetPasswordData.userId}/reset-password`, {
        newPassword: resetPasswordData.newPassword
      });

      setResetSuccess('Password has been reset successfully.');
      // Close after short delay
      setTimeout(() => {
        setActiveModal(null);
        setResetLoading(false);
        setResetSuccess('');
      }, 800);
    } catch (err) {
      setResetLoading(false);
      setResetError(err?.response?.data?.error || err.message || 'Failed to reset password');
    }
  };

  const handleDeleteFromAllTenants = async () => {
    try {
      const response = await axios.delete(`/api/auth/users/${selectedUser.id}`);

      await loadUsers();
      setActiveModal(null);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error deleting user from all tenants:', error);
      setError(error.message);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDeleteFromSpecificTenant = async (tenantId) => {
    try {
      const tenant = tenants.find(t => t.id === tenantId);
      const response = await axios.delete(`/api/tenants/${tenant.code}/users/${selectedUser.id}`);

      await loadUsers();
      setActiveModal(null);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error deleting user from tenant:', error);
      setError(error.message);
    } finally {
      setConfirmAction(null);
    }
  };

  /**
   * Sync all users bidirectionally across all tenants
   * This function handles bidirectional synchronization between central and tenant databases
   * It syncs users from central to tenant AND from tenant to central (email-based matching)
   */
  const handleSyncAllUsers = async () => {
    setSyncingUsers(true);
    setSyncResults(null);
    setError('');

    try {
      const results = [];
      let totalSynced = 0;
      let totalErrors = 0;
      let totalMatched = 0;
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalTenantUsers = 0;
      let totalCentralUsers = 0;

      // Sync users bidirectionally for each tenant
      for (const tenant of tenants) {
        try {
          const response = await axios.post(`/api/tenants/${tenant.code}/sync-users`);

          const result = response.data;
          results.push({
            tenantCode: tenant.code,
            tenantName: tenant.name,
            success: true,
            ...result
          });
          totalSynced += result.synced || 0;
          totalErrors += result.errors || 0;
          totalMatched += result.matched || 0;
          totalCreated += result.created || 0;
          totalUpdated += result.updated || 0;
          totalTenantUsers += result.tenantUsers || 0;
          totalCentralUsers += result.centralUsers || 0;

        } catch (error) {
          results.push({
            tenantCode: tenant.code,
            tenantName: tenant.name,
            success: false,
            error: error.message
          });
          totalErrors++;
        }
      }

      setSyncResults({
        totalSynced,
        totalErrors,
        totalMatched,
        totalCreated,
        totalUpdated,
        totalTenantUsers,
        totalCentralUsers,
        results,
        syncType: 'bidirectional'
      });

      // Refresh user data
      await loadUsers();

    } catch (error) {
      console.error('Error syncing users:', error);
      setError(error.message);
    } finally {
      setSyncingUsers(false);
    }
  };

  /**
   * Sync users from tenant databases to central database (email-based matching)
   * This handles cases where tenant databases are copied from other servers with different CentralUserIDs
   */
  const handleSyncFromTenants = async () => {
    setSyncingUsers(true);
    setSyncResults(null);
    setError('');

    try {
      const results = [];
      let totalSynced = 0;
      let totalErrors = 0;
      let totalMatched = 0;
      let totalCreated = 0;

      // Sync users from each tenant to central database
      for (const tenant of tenants) {
        try {
          const response = await axios.post(`/api/tenants/${tenant.code}/sync-from-tenant`);

          const result = response.data;
          results.push({
            tenantCode: tenant.code,
            tenantName: tenant.name,
            success: true,
            ...result
          });
          totalSynced += result.synced || 0;
          totalErrors += result.errors || 0;
          totalMatched += result.matched || 0;
          totalCreated += result.created || 0;

        } catch (error) {
          results.push({
            tenantCode: tenant.code,
            tenantName: tenant.name,
            success: false,
            error: error.message
          });
          totalErrors++;
        }
      }

      setSyncResults({
        totalSynced,
        totalErrors,
        totalMatched,
        totalCreated,
        results,
        syncType: 'from-tenant'
      });

      // Refresh user data
      await loadUsers();

    } catch (error) {
      console.error('Error syncing users from tenants:', error);
      setError(error.message);
    } finally {
      setSyncingUsers(false);
    }
  };

  // Check for password mismatches across all tenants
  const handleCheckPasswordMismatches = async () => {
    setCheckingMismatches(true);
    setPasswordMismatches({});
    
    try {
      const mismatches = {};
      
      for (const tenant of tenants) {
        try {
          const response = await axios.get(`/api/tenants/${tenant.code}/password-mismatches`);
          const result = response.data;
          
          if (result.mismatches && result.mismatches.length > 0) {
            mismatches[tenant.code] = {
              tenantName: tenant.name,
              mismatches: result.mismatches,
              count: result.count
            };
          }
        } catch (error) {
          console.error(`Error checking password mismatches for tenant ${tenant.code}:`, error);
        }
      }
      
      setPasswordMismatches(mismatches);
    } catch (error) {
      console.error('Error checking password mismatches:', error);
      setError('Failed to check password mismatches');
    } finally {
      setCheckingMismatches(false);
    }
  };

  // Resolve a password mismatch
  const handleResolvePasswordMismatch = async (tenantCode, centralUserId, useTenantPassword) => {
    setResolvingMismatch({ tenantCode, centralUserId });
    
    try {
      const response = await axios.post(`/api/tenants/${tenantCode}/resolve-password-mismatch`, {
        centralUserId,
        useTenantPassword
      });
      
      // Remove this mismatch from the list
      setPasswordMismatches(prev => {
        const updated = { ...prev };
        if (updated[tenantCode]) {
          updated[tenantCode].mismatches = updated[tenantCode].mismatches.filter(
            m => m.centralUserId !== centralUserId
          );
          updated[tenantCode].count = updated[tenantCode].mismatches.length;
          
          // Remove tenant from mismatches if no more mismatches
          if (updated[tenantCode].count === 0) {
            delete updated[tenantCode];
          }
        }
        return updated;
      });
      
      // Show success message
      setError('');
      console.log('Password mismatch resolved successfully');
    } catch (error) {
      console.error('Error resolving password mismatch:', error);
      setError('Failed to resolve password mismatch');
    } finally {
      setResolvingMismatch(null);
    }
  };

  // Check for data conflicts across all tenants
  const handleCheckDataConflicts = async () => {
    setCheckingConflicts(true);
    setDataConflicts({});
    
    try {
      const conflicts = {};
      
      for (const tenant of tenants) {
        try {
          const response = await axios.get(`/api/tenants/${tenant.code}/data-conflicts`);
          const result = response.data;
          
          if (result.conflicts && result.conflicts.length > 0) {
            conflicts[tenant.code] = {
              tenantName: tenant.name,
              conflicts: result.conflicts,
              count: result.count
            };
          }
        } catch (error) {
          console.error(`Error checking data conflicts for tenant ${tenant.code}:`, error);
        }
      }
      
      setDataConflicts(conflicts);
    } catch (error) {
      console.error('Error checking data conflicts:', error);
      setError('Failed to check data conflicts');
    } finally {
      setCheckingConflicts(false);
    }
  };

  // Resolve data conflicts
  const handleResolveDataConflicts = async (tenantCode, conflictResolutions) => {
    setResolvingConflicts(true);
    
    try {
      const response = await axios.post(`/api/tenants/${tenantCode}/resolve-data-conflicts`, {
        conflictResolutions
      });
      
      // Remove resolved conflicts from the list
      setDataConflicts(prev => {
        const updated = { ...prev };
        if (updated[tenantCode]) {
          // Filter out resolved conflicts
          const resolvedCentralUserIds = conflictResolutions.map(r => r.centralUserId);
          updated[tenantCode].conflicts = updated[tenantCode].conflicts.filter(
            conflict => !resolvedCentralUserIds.includes(conflict.centralUserId)
          );
          updated[tenantCode].count = updated[tenantCode].conflicts.length;
          
          // Remove tenant from conflicts if no more conflicts
          if (updated[tenantCode].count === 0) {
            delete updated[tenantCode];
          }
        }
        return updated;
      });
      
      // Show success message
      setError('');
      console.log('Data conflicts resolved successfully');
    } catch (error) {
      console.error('Error resolving data conflicts:', error);
      setError('Failed to resolve data conflicts');
    } finally {
      setResolvingConflicts(false);
    }
  };

  const filteredAndSortedUsers = users
    .filter(user => {
      // Search term filter
      const matchesSearch = !searchTerm || 
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Tenant filter
      const matchesTenant = !tenantFilter || 
        (user.tenants && user.tenants.some(tenant => tenant.id.toString() === tenantFilter));
      
      return matchesSearch && matchesTenant;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'username':
          comparison = a.username.toLowerCase().localeCompare(b.username.toLowerCase());
          break;
        case 'email':
          comparison = a.email.toLowerCase().localeCompare(b.email.toLowerCase());
          break;
        case 'created':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'updated':
          comparison = new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
          break;
        case 'tenants':
          const tenantCountA = a.tenants ? a.tenants.length : 0;
          const tenantCountB = b.tenants ? b.tenants.length : 0;
          comparison = tenantCountA - tenantCountB;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  if (loading && users.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>User Management</h2>
        <div className="header-actions">
          <button 
            className="sync-users-btn"
            onClick={handleSyncAllUsers}
            disabled={syncingUsers}
            title="Bidirectional sync: sync users between central and tenant databases (handles both directions)"
          >
            <span>{syncingUsers ? '🔄' : '🔄'}</span>
            {syncingUsers ? 'Syncing...' : 'Bidirectional Sync'}
          </button>
          <button 
            className="sync-from-tenants-btn"
            onClick={handleSyncFromTenants}
            disabled={syncingUsers}
            title="Sync users from tenant databases to central database (email-based matching)"
          >
            <span>{syncingUsers ? '🔄' : '⬆️'}</span>
            {syncingUsers ? 'Syncing...' : 'Sync from Tenants'}
          </button>
          <button 
            className="check-mismatches-btn"
            onClick={handleCheckPasswordMismatches}
            disabled={checkingMismatches}
            title="Check for password mismatches between central and tenant databases"
          >
            <span>{checkingMismatches ? '🔍' : '🔍'}</span>
            {checkingMismatches ? 'Checking...' : 'Check Password Mismatches'}
          </button>
          <button 
            className="check-conflicts-btn"
            onClick={handleCheckDataConflicts}
            disabled={checkingConflicts}
            title="Check for data conflicts between central and tenant databases (name, email, phone, etc.)"
          >
            <span>{checkingConflicts ? '⚠️' : '⚠️'}</span>
            {checkingConflicts ? 'Checking...' : 'Check Data Conflicts'}
          </button>
          <button 
            className="bulk-assign-btn"
            onClick={() => setActiveModal('bulk')}
          >
            <span>👥</span>
            Bulk Assignment
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {syncResults && (
        <div className={`sync-results ${syncResults.totalErrors > 0 ? 'with-errors' : 'success'}`}>
          <div className="sync-summary">
            <h4>
              {syncResults.syncType === 'from-tenant' ? 'Tenant-to-Central Sync Results' : 
               syncResults.syncType === 'bidirectional' ? 'Bidirectional Sync Results' : 
               'User Sync Results'}
            </h4>
            <div className="sync-stats">
              <span className="stat">
                <strong>Total Synced:</strong> {syncResults.totalSynced}
              </span>
              <span className="stat">
                <strong>Total Errors:</strong> {syncResults.totalErrors}
              </span>
              {(syncResults.syncType === 'from-tenant' || syncResults.syncType === 'bidirectional') && (
                <>
                  <span className="stat">
                    <strong>Matched by Email:</strong> {syncResults.totalMatched || 0}
                  </span>
                  <span className="stat">
                    <strong>Created New:</strong> {syncResults.totalCreated || 0}
                  </span>
                </>
              )}
              {syncResults.syncType === 'bidirectional' && (
                <>
                  <span className="stat">
                    <strong>Updated:</strong> {syncResults.totalUpdated || 0}
                  </span>
                  <span className="stat">
                    <strong>Tenant Users:</strong> {syncResults.totalTenantUsers || 0}
                  </span>
                  <span className="stat">
                    <strong>Central Users:</strong> {syncResults.totalCentralUsers || 0}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="sync-details">
            {syncResults.results.map((result, index) => (
              <div key={index} className={`tenant-result ${result.success ? 'success' : 'error'}`}>
                <span className="tenant-name">{result.tenantName} ({result.tenantCode})</span>
                {result.success ? (
                  <span className="result-text">
                    ✅ {result.synced || 0} users synced, {result.errors || 0} errors
                    {(syncResults.syncType === 'from-tenant' || syncResults.syncType === 'bidirectional') && (
                      <span className="sync-breakdown">
                        ({result.matched || 0} matched, {result.created || 0} created
                        {syncResults.syncType === 'bidirectional' && `, ${result.updated || 0} updated`})
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="result-text error">
                    ❌ {result.error}
                  </span>
                )}
              </div>
            ))}
          </div>
          <button 
            className="close-sync-results"
            onClick={() => setSyncResults(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Password Mismatches Display */}
      {Object.keys(passwordMismatches).length > 0 && (
        <div className="password-mismatches">
          <div className="mismatches-header">
            <h3>🔐 Password Mismatches Detected</h3>
            <p>Users with different passwords in central and tenant databases</p>
          </div>
          
          {Object.entries(passwordMismatches).map(([tenantCode, data]) => (
            <div key={tenantCode} className="tenant-mismatches">
              <h4>{data.tenantName} ({tenantCode}) - {data.count} mismatch{data.count !== 1 ? 'es' : ''}</h4>
              
              <div className="mismatches-list">
                {data.mismatches.map((mismatch) => (
                  <div key={mismatch.centralUserId} className="mismatch-item">
                    <div className="mismatch-info">
                      <strong>{mismatch.username}</strong> ({mismatch.email})
                      <br />
                      <small>Central User ID: {mismatch.centralUserId}</small>
                    </div>
                    
                    <div className="mismatch-actions">
                      <button
                        className="resolve-btn use-tenant"
                        onClick={() => handleResolvePasswordMismatch(tenantCode, mismatch.centralUserId, true)}
                        disabled={resolvingMismatch?.centralUserId === mismatch.centralUserId}
                        title="Use the password from tenant database"
                      >
                        {resolvingMismatch?.centralUserId === mismatch.centralUserId ? 'Resolving...' : 'Use Tenant Password'}
                      </button>
                      
                      <button
                        className="resolve-btn use-central"
                        onClick={() => handleResolvePasswordMismatch(tenantCode, mismatch.centralUserId, false)}
                        disabled={resolvingMismatch?.centralUserId === mismatch.centralUserId}
                        title="Use the password from central database"
                      >
                        {resolvingMismatch?.centralUserId === mismatch.centralUserId ? 'Resolving...' : 'Use Central Password'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          <div className="mismatches-footer">
            <button 
              className="close-mismatches"
              onClick={() => setPasswordMismatches({})}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Data Conflicts Display */}
      {Object.keys(dataConflicts).length > 0 && (
        <div className="data-conflicts">
          <div className="conflicts-header">
            <h3>⚠️ Data Conflicts Detected</h3>
            <p>Users with different data between central and tenant databases</p>
          </div>
          
          {Object.entries(dataConflicts).map(([tenantCode, data]) => (
            <div key={tenantCode} className="tenant-conflicts">
              <h4>{data.tenantName} ({tenantCode}) - {data.count} conflict{data.count !== 1 ? 's' : ''}</h4>
              
              <div className="conflicts-list">
                {data.conflicts.map((conflict) => (
                  <div key={conflict.centralUserId} className="conflict-item">
                    <div className="conflict-info">
                      <strong>{conflict.email}</strong>
                      <br />
                      <small>Central User ID: {conflict.centralUserId}, Tenant User ID: {conflict.tenantUserId}</small>
                    </div>
                    
                    <div className="conflict-fields">
                      {conflict.conflicts.map((fieldConflict, index) => (
                        <div key={index} className="field-conflict">
                          <div className="field-name">
                            <strong>{fieldConflict.field}</strong>
                            <span className={`conflict-type ${fieldConflict.conflictType}`}>
                              {fieldConflict.conflictType === 'mismatch' ? 'Different Values' :
                               fieldConflict.conflictType === 'missing_central' ? 'Missing in Central' :
                               fieldConflict.conflictType === 'missing_tenant' ? 'Missing in Tenant' : 'Conflict'}
                            </span>
                          </div>
                          
                          <div className="field-values">
                            <div className="value-option">
                              <label>
                                <input
                                  type="radio"
                                  name={`${conflict.centralUserId}-${fieldConflict.field}`}
                                  value="central"
                                  defaultChecked={fieldConflict.conflictType === 'missing_tenant'}
                                />
                                <span className="value-label">Central:</span>
                                <span className="value-text">
                                  {fieldConflict.centralValue || '(empty)'}
                                </span>
                              </label>
                            </div>
                            
                            <div className="value-option">
                              <label>
                                <input
                                  type="radio"
                                  name={`${conflict.centralUserId}-${fieldConflict.field}`}
                                  value="tenant"
                                  defaultChecked={fieldConflict.conflictType === 'missing_central'}
                                />
                                <span className="value-label">Tenant:</span>
                                <span className="value-text">
                                  {fieldConflict.tenantValue || '(empty)'}
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="conflict-actions">
                <button
                  className="resolve-conflicts-btn"
                  onClick={() => {
                    const conflictResolutions = [];
                    
                    data.conflicts.forEach(conflict => {
                      conflict.conflicts.forEach(fieldConflict => {
                        const selectedValue = document.querySelector(
                          `input[name="${conflict.centralUserId}-${fieldConflict.field}"]:checked`
                        )?.value;
                        
                        if (selectedValue) {
                          conflictResolutions.push({
                            centralUserId: conflict.centralUserId,
                            tenantUserId: conflict.tenantUserId,
                            field: fieldConflict.field,
                            useCentralValue: selectedValue === 'central',
                            centralValue: fieldConflict.centralValue,
                            tenantValue: fieldConflict.tenantValue
                          });
                        }
                      });
                    });
                    
                    if (conflictResolutions.length > 0) {
                      handleResolveDataConflicts(tenantCode, conflictResolutions);
                    }
                  }}
                  disabled={resolvingConflicts}
                >
                  {resolvingConflicts ? 'Resolving...' : `Resolve ${data.count} Conflict${data.count !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          ))}
          
          <div className="conflicts-footer">
            <button 
              className="close-conflicts"
              onClick={() => setDataConflicts({})}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="user-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search users by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="tenant-filter">Filter by Tenant:</label>
            <select
              id="tenant-filter"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Tenants</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.code})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="sort-by">Sort by:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Name</option>
              <option value="username">Username</option>
              <option value="email">Email</option>
              <option value="created">Created Date</option>
              <option value="updated">Last Updated</option>
              <option value="tenants">Number of Tenants</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="sort-order">Order:</label>
            <select
              id="sort-order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="filter-select"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          
          <div className="filter-reset">
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setTenantFilter('');
                setSortBy('name');
                setSortOrder('asc');
              }}
              className="reset-filters-btn"
              title="Reset all filters and sorting"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Username</th>
              <th>Email</th>
                                  <th>Tenants</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </div>
                    <div className="user-details">
                      <div className="user-name">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="user-roles">
                        {user.roles && user.roles.length > 0 ? (
                          sortRolesByHierarchy(user.roles).map((role, index) => {
                            const roleName = role.RoleName || role.roleName || role;
                            return (
                              <span key={index} className="role-badge" title={getRoleDisplayName(roleName)}>
                                {roleName}
                                {index < user.roles.length - 1 && ', '}
                              </span>
                            );
                          })
                        ) : (
                          <span className="no-roles">No roles</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  <div className="user-tenants">
                    {user.tenants?.length || 0} tenants
                  </div>
                </td>
                <td>
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="view-user-btn"
                      onClick={() => {
                        const userTenants = getUserTenants(user);
                        setSelectedUser({
                          ...user,
                          tenants: userTenants
                        });
                        setActiveModal('details');
                      }}
                    >
                      View Details
                    </button>
                    <button
                      className="reset-password-btn"
                      onClick={() => handleOpenResetPassword(user)}
                      title="Reset/Change Password"
                    >
                      Reset Password
                    </button>
                    <button
                      className="delete-user-btn"
                      onClick={() => handleDeleteUser(user)}
                      title="Delete User"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAndSortedUsers.length === 0 && (
          <div className="no-users">
            <p>
              {searchTerm || tenantFilter
                ? `No users found matching the current filters`
                : 'No users found'
              }
            </p>
          </div>
        )}
      </div>

      {/* Bulk Assignment Modal */}
      {activeModal === 'bulk' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Bulk User Assignment</h3>
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleBulkAssignment} className="modal-form">
              <div className="form-group">
                <label>Select Users *</label>
                <div className="user-selection">
                  {users.map((user) => (
                    <label key={user.id} className="user-checkbox">
                      <input
                        type="checkbox"
                        checked={bulkAssignment.userIds.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkAssignment({
                              ...bulkAssignment,
                              userIds: [...bulkAssignment.userIds, user.id]
                            });
                          } else {
                            setBulkAssignment({
                              ...bulkAssignment,
                              userIds: bulkAssignment.userIds.filter(id => id !== user.id)
                            });
                          }
                        }}
                      />
                      {user.firstName} {user.lastName} ({user.username})
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Tenant *</label>
                <select
                  value={bulkAssignment.tenantId}
                  onChange={(e) => setBulkAssignment({
                    ...bulkAssignment,
                    tenantId: e.target.value
                  })}
                  required
                >
                  <option value="">Select tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Access Level *</label>
                <select
                  value={bulkAssignment.accessLevel}
                  onChange={(e) => setBulkAssignment({
                    ...bulkAssignment,
                    accessLevel: e.target.value
                  })}
                  required
                >
                  <option value="Read">Read Access</option>
                  <option value="Write">Write Access</option>
                  <option value="Admin">Admin Access</option>
                </select>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setActiveModal(null)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="assign-btn"
                  disabled={loading || bulkAssignment.userIds.length === 0}
                >
                  {loading ? 'Assigning...' : `Assign ${bulkAssignment.userIds.length} Users`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {activeModal === 'details' && selectedUser && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>{selectedUser.firstName} {selectedUser.lastName}</h3>
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <div className="user-details">
              <div className="details-section">
                <h4>User Information</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Username:</span>
                    <span className="detail-value">{selectedUser.username}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedUser.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Full Name:</span>
                    <span className="detail-value">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Roles:</span>
                    <span className="detail-value">
                      {selectedUser.roles && selectedUser.roles.length > 0 ? (
                        <div className="roles-list">
                          {sortRolesByHierarchy(selectedUser.roles).map((role, index) => {
                            const roleName = role.RoleName || role.roleName || role;
                            const rolePermissions = roleConfig.permissions?.[roleName] || [];
                            return (
                              <div key={index} className="role-detail-item">
                                <span className="role-name">{getRoleDisplayName(roleName)}</span>
                                {rolePermissions.length > 0 && (
                                  <span className="role-permissions-count" title={rolePermissions.join(', ')}>
                                    ({rolePermissions.length} permissions)
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="no-roles">No roles assigned</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {new Date(selectedUser.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h4>Tenant Access ({selectedUser.tenants?.length || 0})</h4>
                {selectedUser.tenants && selectedUser.tenants.length > 0 ? (
                  <div className="user-tenants-list">
                    {selectedUser.tenants.map((tenant) => (
                      <div key={tenant.id} className="user-tenant-item">
                        <div className="tenant-header">
                          <div className="tenant-name-section">
                            <h5>{tenant.name}</h5>
                            <span className="tenant-code">{tenant.code}</span>
                          </div>
                          <div className="tenant-badges">
                            <span className={`access-badge ${tenant.accessLevel.toLowerCase()}`}>
                              {tenant.accessLevel}
                            </span>
                            {tenant.isDefault && (
                              <span className="default-badge">⭐ Default</span>
                            )}
                            <span className={`status-badge ${tenant.isActive ? 'active' : 'locked'}`}>
                              {tenant.isActive ? '✓ Active' : '🔒 Locked'}
                            </span>
                          </div>
                        </div>
                        
                        {!tenant.isActive && (
                          <div className="tenant-warning">
                            ⚠️ This user is locked in this tenant and cannot log in
                          </div>
                        )}
                        
                        <div className="tenant-actions">
                          <button
                            className="sync-user-btn"
                            onClick={async () => {
                              try {
                                const response = await axios.post(`/api/tenants/${tenant.code}/sync-users`);
                                const result = response.data;
                                alert(`User sync completed for ${tenant.name}: ${result.synced} users synced, ${result.errors} errors`);
                                // Reload user details to get updated status
                                await loadUsers();
                                const updatedUser = users.find(u => u.id === selectedUser.id);
                                if (updatedUser) {
                                  setSelectedUser({
                                    ...updatedUser,
                                    tenants: getUserTenants(updatedUser)
                                  });
                                }
                              } catch (error) {
                                alert(`Sync failed: ${error.response?.data?.error || error.message}`);
                              }
                            }}
                            title="Sync user information to this tenant"
                          >
                            🔄 Sync
                          </button>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveUserFromTenant(selectedUser.id, tenant.id)}
                            title="Remove user from this tenant"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-tenant-access">
                    <p>📭 This user has no tenant access.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Options Modal */}
      {activeModal === 'deleteOptions' && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete User: {selectedUser.firstName} {selectedUser.lastName}</h3>
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>This user is assigned to multiple tenants. Choose how you want to delete the user:</p>
              
              <div className="deletion-options">
                <div className="deletion-option">
                  <h4>Delete from All Tenants</h4>
                  <p>Permanently delete the user from all systems. This action cannot be undone.</p>
                  <button
                    className="delete-all-btn danger-btn"
                    onClick={() => {
                      setConfirmAction({
                        title: 'Delete User from All Tenants',
                        message: `Are you sure you want to permanently delete ${selectedUser.firstName} ${selectedUser.lastName} from all tenants? This action cannot be undone.`,
                        action: handleDeleteFromAllTenants
                      });
                      setActiveModal(null);
                    }}
                  >
                    Delete from All Tenants
                  </button>
                </div>

                <div className="deletion-option">
                  <h4>Delete from Specific Tenant</h4>
                  <p>Remove the user from only one tenant while preserving access to others.</p>
                  
                  <div className="tenant-selection">
                    {selectedUser.tenants && selectedUser.tenants.map((tenant) => (
                      <div key={tenant.id} className="tenant-delete-item">
                        <div className="tenant-info">
                          <span className="tenant-name">{tenant.name}</span>
                          <span className="tenant-code">({tenant.code})</span>
                          <span className={`access-badge ${tenant.accessLevel.toLowerCase()}`}>
                            {tenant.accessLevel}
                          </span>
                        </div>
                        <button
                          className="delete-tenant-btn"
                          onClick={() => {
                            setConfirmAction({
                              title: 'Delete User from Tenant',
                              message: `Are you sure you want to remove ${selectedUser.firstName} ${selectedUser.lastName} from ${tenant.name}?`,
                              action: () => handleDeleteFromSpecificTenant(tenant.id)
                            });
                            setActiveModal(null);
                          }}
                        >
                          Remove from {tenant.code}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setActiveModal(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{confirmAction.title}</h3>
            </div>
            <div className="modal-body">
              <p>{confirmAction.message}</p>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={confirmAction.action}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {activeModal === 'resetPassword' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Reset User Password</h3>
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleResetPasswordSubmit} className="modal-form">
              {resetError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>
                  {resetError}
                </div>
              )}
              {resetSuccess && (
                <div className="success-message" style={{ marginBottom: '12px' }}>
                  {resetSuccess}
                </div>
              )}
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({
                    ...resetPasswordData,
                    newPassword: e.target.value
                  })}
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData({
                    ...resetPasswordData,
                    confirmPassword: e.target.value
                  })}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setActiveModal(null)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="assign-btn"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;